import { Add as AddIcon } from '@mui/icons-material';
import { Alert, Box, Button, Stack } from '@mui/material';
import React from 'react';
import CommentIconComponent from './CommentIcon';
import ReviewContentEditDialogs from './ReviewContentEditDialogs';
import ReviewContentEditSection from './ReviewContentEditSection';
import { ReviewContentEditViewProps } from './reviewContentEditViewTypes';
import { mergeDraftSectionsWithAcceptedAdditions } from './reviewContentEditViewUtils';
import { useReviewContentEditView } from './useReviewContentEditView';

export type { ReviewContentEditViewProps } from './reviewContentEditViewTypes';

/**
 * Editable view for reviewing content.
 * Each section can be edited individually with comments required for changes.
 */
export default function ReviewContentEditView({
  publishedContent,
  draftContent,
  reviewData,
  onReviewDataUpdate,
  currentUser,
  modelType = 'FundOpportunity',
  tone = 'tone1',
  canAddContent = false,
}: ReviewContentEditViewProps) {
  const controller = useReviewContentEditView({
    publishedContent,
    draftContent,
    reviewData,
    onReviewDataUpdate,
    currentUser,
    modelType,
    tone,
    canAddContent,
  });

  const allSections = mergeDraftSectionsWithAcceptedAdditions(draftContent, reviewData);

  return (
    <>
      <Alert severity="info" sx={{ mb: 3 }}>
        <strong>Review Mode:</strong> Edit any section below. Changes are saved as proposed
        modifications and require a comment. Original content is not affected.
      </Alert>

      {controller.hasUnsavedChanges && canAddContent && (
        <Alert severity="warning" sx={{ mb: 3 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
            }}
          >
            <Box>
              <strong>Unsaved Changes:</strong> You have unsaved changes that need to be saved to
              draft.
            </Box>
            <Button
              variant="contained"
              color="primary"
              onClick={controller.handleSaveAllChangesToDraft}
              disabled={controller.submitting}
            >
              {controller.submitting ? 'Saving...' : 'Save All Changes to Draft'}
            </Button>
          </Box>
        </Alert>
      )}

      <Stack spacing={2} sx={{ position: 'relative' }}>
        {allSections.map((item, index) => (
          <React.Fragment key={`section-${index}`}>
            {canAddContent && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => controller.handleAddContent(index)}
                  startIcon={<AddIcon />}
                >
                  Add Content Above
                </Button>
              </Box>
            )}
            <ReviewContentEditSection
              item={item}
              index={index}
              tone={tone}
              modelType={modelType}
              draftContent={draftContent}
              reviewData={reviewData}
              currentUser={currentUser}
              editingSectionId={controller.editingSectionId}
              editedContent={controller.editedContent}
              submitting={controller.submitting}
              showMinimalReply={controller.showMinimalReply}
              minimalReplyText={controller.minimalReplyText}
              onEditStart={controller.handleEditStart}
              onEditCancel={controller.handleEditCancel}
              onEditSave={controller.handleEditSave}
              onRevertToDraft={controller.handleRevertToDraft}
              onReplaceComponent={controller.handleReplaceComponent}
              onViewOriginalComponent={controller.handleViewOriginalComponent}
              onEditedContentChange={controller.setEditedContent}
              onShowMinimalReply={controller.setShowMinimalReply}
              onMinimalReplyTextChange={controller.setMinimalReplyText}
              onMinimalReplySubmit={controller.handleMinimalReplySubmit}
              onMinimalReplyCancel={controller.handleMinimalReplyCancel}
              onReviewDataUpdate={onReviewDataUpdate}
            />
          </React.Fragment>
        ))}
        {canAddContent && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 1 }}>
            <Button
              variant="outlined"
              size="small"
              onClick={() => controller.handleAddContent(draftContent.content.length)}
              startIcon={<AddIcon />}
            >
              Add Content at End
            </Button>
          </Box>
        )}
      </Stack>

      {controller.commentOverlays.length > 0 && (
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            pointerEvents: 'none',
            zIndex: 20,
          }}
        >
          {controller.commentOverlays.map(({ conversation, top, right }) => (
            <Box
              key={conversation.conversationId}
              sx={{
                position: 'absolute',
                top: `${top}px`,
                right: `${right}px`,
                pointerEvents: 'auto',
              }}
            >
              <CommentIconComponent conversation={conversation} tone={tone} />
            </Box>
          ))}
        </Box>
      )}

      <ReviewContentEditDialogs
        tone={tone}
        modelType={modelType}
        draftContent={draftContent}
        submitting={controller.submitting}
        showCommentDialog={controller.dialogState.showCommentDialog}
        comment={controller.dialogState.comment}
        showAddCommentDialog={controller.dialogState.showAddCommentDialog}
        addCommentText={controller.dialogState.addCommentText}
        showAddContentDialog={controller.dialogState.showAddContentDialog}
        selectedContentType={controller.dialogState.selectedContentType}
        showReplaceComponentDialog={controller.dialogState.showReplaceComponentDialog}
        selectedReplaceContentType={controller.dialogState.selectedReplaceContentType}
        previousComponent={controller.dialogState.previousComponent}
        showPreviousComponentDialog={controller.dialogState.showPreviousComponentDialog}
        showOriginalComponentDialog={controller.dialogState.showOriginalComponentDialog}
        originalComponentContent={controller.dialogState.originalComponentContent}
        onCommentChange={controller.setComment}
        onCommentSubmit={controller.handleCommentSubmit}
        onCommentCancel={controller.handleCommentCancel}
        onAddCommentTextChange={controller.setAddCommentText}
        onAddCommentSubmit={controller.handleAddCommentSubmit}
        onAddCommentCancel={controller.handleAddCommentCancel}
        onSelectedContentTypeChange={controller.setSelectedContentType}
        onAddContentSubmit={controller.handleAddContentSubmit}
        onAddContentCancel={controller.handleAddContentCancel}
        onSelectedReplaceContentTypeChange={controller.setSelectedReplaceContentType}
        onReplaceComponentSubmit={controller.handleReplaceComponentSubmit}
        onReplaceComponentCancel={controller.handleReplaceComponentCancel}
        onViewPreviousComponent={controller.handleViewPreviousComponent}
        onClosePreviousComponent={controller.handleClosePreviousComponent}
        onCloseOriginalComponent={controller.handleCloseOriginalComponent}
      />
    </>
  );
}
